import React, { useState } from 'react';
import { Modal, Table, Select, Alert, Row, Col, Card, Statistic, Collapse, Tag, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Panel } = Collapse;

interface Candidate {
  product_id: number;
  internal_product_code: string;
  client_product_code: string | null;
  product_description: string;
  size: string | null;
  color: string | null;
}

interface MatchedProduct {
  row: number;
  code_1: string;
  code_2: string;
  description_excel: string;
  product_id: number;
  product_description: string;
  internal_product_code: string;
  client_product_code: string | null;
  size: string | null;
  color: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  total_mismatch: boolean;
  excel_total: number;
  match_type: string;
}

interface NeedsResolution {
  row: number;
  code_1: string;
  code_2: string;
  description_excel: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  excel_total: number;
  candidates: Candidate[];
}

interface NotFoundProduct {
  row: number;
  code_1: string;
  code_2: string;
  description_excel: string;
  quantity: number;
  unit_price: number;
  excel_total: number;
}

interface SkippedRow {
  row: number;
  reason: string;
  description_excel: string;
}

interface TotalMismatchWarning {
  row: number;
  code_1: string;
  excel_total: number;
  calculated_total: number;
}

interface ImportResult {
  status: string;
  matched_products: MatchedProduct[];
  needs_resolution: NeedsResolution[];
  not_found: NotFoundProduct[];
  skipped: SkippedRow[];
  total_mismatch_warnings: TotalMismatchWarning[];
  summary: {
    total_rows: number;
    matched: number;
    needs_resolution: number;
    not_found: number;
    skipped: number;
  };
}

interface SilverseaExcelImportModalProps {
  visible: boolean;
  importResult: ImportResult;
  onConfirm: (resolutions: Record<number, number>) => void;
  onCancel: () => void;
}

const SilverseaExcelImportModal: React.FC<SilverseaExcelImportModalProps> = ({
  visible,
  importResult,
  onConfirm,
  onCancel
}) => {
  const [resolutions, setResolutions] = useState<Record<number, number>>({});

  const handleResolutionChange = (row: number, productId: number) => {
    setResolutions(prev => ({ ...prev, [row]: productId }));
  };

  const handleConfirm = () => {
    // Verifica che tutti i prodotti da risolvere abbiano una selezione
    const unresolved = importResult.needs_resolution.filter(item => !resolutions[item.row]);
    if (unresolved.length > 0) {
      message.warning(`Seleziona il prodotto corretto per ${unresolved.length} righe non ancora risolte`);
      return;
    }
    onConfirm(resolutions);
  };

  const formatProductOption = (candidate: Candidate): string => {
    let display = candidate.internal_product_code;
    if (candidate.size) display += ` - ${candidate.size}`;
    if (candidate.color) display += ` - ${candidate.color}`;
    display += ` - ${candidate.product_description}`;
    if (candidate.client_product_code) display += ` (${candidate.client_product_code})`;
    return display;
  };

  // Colonne per la tabella di risoluzione
  const resolutionColumns = [
    {
      title: 'Riga',
      dataIndex: 'row',
      width: 60,
    },
    {
      title: 'CODE_1',
      dataIndex: 'code_1',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'CODE_2',
      dataIndex: 'code_2',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Descrizione Excel',
      dataIndex: 'description_excel',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Qta',
      dataIndex: 'quantity',
      width: 70,
    },
    {
      title: 'Prodotto da associare',
      width: 350,
      render: (_: any, record: NeedsResolution) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Seleziona prodotto"
          value={resolutions[record.row]}
          onChange={(value) => handleResolutionChange(record.row, value)}
          showSearch
          optionFilterProp="children"
        >
          {record.candidates.map((candidate) => (
            <Option key={candidate.product_id} value={candidate.product_id}>
              {formatProductOption(candidate)}
            </Option>
          ))}
        </Select>
      ),
    },
  ];

  // Colonne per prodotti trovati
  const matchedColumns = [
    { title: 'Riga', dataIndex: 'row', width: 60 },
    { title: 'CODE_1', dataIndex: 'code_1', width: 120, ellipsis: true },
    { title: 'CODE_2', dataIndex: 'code_2', width: 120, ellipsis: true },
    { title: 'Descrizione Excel', dataIndex: 'description_excel', width: 200, ellipsis: true },
    { title: 'Prodotto DB', dataIndex: 'product_description', width: 200, ellipsis: true },
    { title: 'Qta', dataIndex: 'quantity', width: 70 },
    { title: 'Vendita', dataIndex: 'unit_price', width: 90, render: (v: number) => `€ ${v?.toFixed(2)}` },
    {
      title: 'Match',
      dataIndex: 'match_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'client_code' ? 'green' : 'blue'}>
          {type === 'client_code' ? 'CODE_1' : 'CODE_2'}
        </Tag>
      ),
    },
  ];

  // Colonne per prodotti non trovati
  const notFoundColumns = [
    { title: 'Riga', dataIndex: 'row', width: 60 },
    { title: 'CODE_1', dataIndex: 'code_1', width: 120, ellipsis: true },
    { title: 'CODE_2', dataIndex: 'code_2', width: 120, ellipsis: true },
    { title: 'Descrizione Excel', dataIndex: 'description_excel', width: 300, ellipsis: true },
    { title: 'Qta', dataIndex: 'quantity', width: 70 },
    { title: 'Vendita', dataIndex: 'unit_price', width: 90, render: (v: number) => `€ ${v?.toFixed(2)}` },
  ];

  const { summary } = importResult;

  return (
    <Modal
      title="Importazione Prodotti da Excel - Risultati Analisi"
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText="Importa Prodotti"
      cancelText="Annulla"
      width={1200}
      destroyOnClose
      okButtonProps={{
        disabled: importResult.matched_products.length === 0 && importResult.needs_resolution.length === 0
      }}
    >
      {/* Riepilogo */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Trovati"
              value={summary.matched}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Da Risolvere"
              value={summary.needs_resolution}
              valueStyle={{ color: summary.needs_resolution > 0 ? '#faad14' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Non Trovati"
              value={summary.not_found}
              valueStyle={{ color: summary.not_found > 0 ? '#ff4d4f' : undefined }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic
              title="Saltati"
              value={summary.skipped}
              prefix={<FilterOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Totale Righe"
              value={summary.total_rows}
            />
          </Card>
        </Col>
      </Row>

      {/* Warning TOTAL mismatch */}
      {importResult.total_mismatch_warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${importResult.total_mismatch_warnings.length} righe con totale non corrispondente`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {importResult.total_mismatch_warnings.map((w, i) => (
                <li key={i}>
                  Riga {w.row} ({w.code_1}): Totale Excel = € {w.excel_total?.toFixed(2)}, Calcolato = € {w.calculated_total?.toFixed(2)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {/* Sezione Risoluzione Prodotti Ambigui */}
      {importResult.needs_resolution.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ color: '#faad14' }}>
            <WarningOutlined /> Prodotti da risolvere ({importResult.needs_resolution.length})
          </h4>
          <p style={{ color: '#666', marginBottom: 8 }}>
            I seguenti prodotti hanno match multipli nel database. Confronta la descrizione del file Excel con le descrizioni dei prodotti e seleziona quello corretto.
          </p>
          <Table
            dataSource={importResult.needs_resolution}
            columns={resolutionColumns}
            rowKey="row"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}

      {/* Sezione Prodotti Non Trovati */}
      {importResult.not_found.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${importResult.not_found.length} prodotti non trovati nel database`}
          description="I seguenti prodotti non verranno importati nell'ordine."
        />
      )}
      {importResult.not_found.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Table
            dataSource={importResult.not_found}
            columns={notFoundColumns}
            rowKey="row"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 'max-content' }}
          />
        </div>
      )}

      {/* Sezione Prodotti Trovati (collassabile) */}
      {importResult.matched_products.length > 0 && (
        <Collapse style={{ marginBottom: 16 }}>
          <Panel
            header={`Prodotti trovati automaticamente (${importResult.matched_products.length})`}
            key="matched"
          >
            <Table
              dataSource={importResult.matched_products}
              columns={matchedColumns}
              rowKey="row"
              pagination={false}
              size="small"
              bordered
              scroll={{ x: 'max-content' }}
            />
          </Panel>
        </Collapse>
      )}
    </Modal>
  );
};

export default SilverseaExcelImportModal;
